import { useMutation, useQueryClient } from "@tanstack/react-query";
import { httpClient } from "@/shared/api/http-client";
import { PENDING_BALANCES_QUERY_KEY } from "./constants";
import type { LiquidateInput, LiquidateResponse } from "./types";

/**
 * Hand-written fetch — see CLAUDE.md → TD-07. `POST …/venues/:id/liquidate`
 * (API-FIN-3, WANDERFIT_ADMIN only, audited) writes off ONE currency's pending
 * balance to WanderFit retention — it does NOT pay the venue. `reason` is
 * required (5–500 chars). No-balance → 404; race lost → 200 with
 * `liquidatedCents: "0"`. Naturally idempotent (settles the accruals).
 */
async function liquidateVenue({
  venueId,
  currency,
  reason,
}: LiquidateInput): Promise<LiquidateResponse> {
  return httpClient<LiquidateResponse>(
    `/api/admin/payouts/venues/${encodeURIComponent(venueId)}/liquidate`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ currency, reason: reason.trim() }),
    },
  );
}

export function useLiquidateVenue() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: liquidateVenue,
    onSuccess: () => {
      // The written-off currency drops off the pending report — refetch.
      queryClient.invalidateQueries({ queryKey: PENDING_BALANCES_QUERY_KEY });
    },
  });
}
